// Display helpers for the voice profile. Words for numbers, never scoring — the API measured,
// the platform verified; this file only says it in the person's language.

import type { RegisterDimensions } from '../types/voice'

export const REGISTER_AXES: { key: keyof RegisterDimensions; label: string; low: string; high: string }[] = [
  { key: 'involved', label: 'Involved', low: 'informational', high: 'personal' },
  { key: 'narrative', label: 'Narrative', low: 'in the moment', high: 'tells what happened' },
  { key: 'explicit', label: 'Explicit', low: 'assumes context', high: 'spells it out' },
  { key: 'persuasive', label: 'Persuasive', low: 'states', high: 'argues' },
  { key: 'abstract', label: 'Abstract', low: 'concrete', high: 'abstract' },
  { key: 'formal', label: 'Formal', low: 'everyday words', high: 'formal words' },
]

export function percent(rate: number): string {
  return `${Math.round(Math.max(0, Math.min(1, rate)) * 100)}%`
}

export function rateWords(rate: number, low: string, mid: string, high: string): string {
  return rate <= 0.01 ? low : rate < 0.08 ? mid : high
}

/** "email/external/request" → "Email · to clients and contacts · asking for something". */
export function scenarioLabel(scenario: string): string {
  if (scenario === 'all') return 'Everything you send'
  const [channel, audience, intent] = scenario.split('/')
  return [channelWord(channel), audienceWord(audience), intentWord(intent)].filter(Boolean).join(' · ')
}

export function channelWord(channel?: string): string {
  return channel === 'email' ? 'Email' : channel ?? ''
}

export function audienceWord(audience?: string): string {
  switch (audience) {
    case 'colleague':
      return 'to colleagues'
    case 'external':
      return 'to clients and contacts'
    case 'client':
      return 'to clients'
    case 'prospect':
      return 'to prospects'
    case 'leadership':
      return 'to leadership'
    case 'vendor':
      return 'to vendors'
    case 'personal':
      return 'personal'
    case 'automated':
      return 'automated'
    case 'all':
      return ''
    default:
      return audience ?? ''
  }
}

export function intentWord(intent?: string): string {
  switch (intent) {
    case 'request':
      return 'asking for something'
    case 'propose':
      return 'proposing a time or an idea'
    case 'commit':
      return 'promising a next step'
    case 'deliver':
      return 'sending what you promised'
    case 'thank':
      return 'saying thanks'
    case 'reconnect':
      return 'reconnecting'
    case 'introduce':
      return 'making an introduction'
    case 'seasonal':
      return 'holiday and season notes'
    case 'status':
      return 'giving an update'
    case 'other':
      return 'everything else'
    case 'all':
      return ''
    default:
      return intent ?? ''
  }
}

/** A verifier score in the person's language. */
export function verdictWords(score: number, passes: boolean): string {
  if (passes && score >= 0.9) return 'Reads like you'
  if (passes) return 'Close to you'
  if (score >= 0.5) return 'Not quite you'
  return 'Not you'
}

export function clampScore(score: number): number {
  return Math.max(0, Math.min(1, score))
}
